/**
 * Página pública para compartir logros especiales
 * 
 * URL: /share/futbolista/:futbolistaId/logro-especial/:logroEspecialId
 * 
 * Muestra el logro especial conseguido por un futbolista
 * con diseño optimizado para compartir en redes sociales
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ShareSocialButton from '../../components/common/ShareSocialButton';
import '../../styles/share-logros.css';

const ShareLogroEspecial = () => {
  const { futbolistaId, logroEspecialId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLogroEspecial = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `/api/postgres/share/futbolista/${futbolistaId}/logro-especial/${logroEspecialId}`
        );

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setData(result.data);
          } else {
            setError(result.message);
          }
        } else {
          setError('No se pudo cargar el logro especial');
        }
      } catch (err) {
        console.error('Error cargando logro especial:', err);
        setError('Error al cargar el logro especial');
      } finally {
        setLoading(false);
      }
    };

    fetchLogroEspecial();
  }, [futbolistaId, logroEspecialId]);

  if (loading) {
    return (
      <div className="share-logros-container">
        <div className="share-logros-loading">
          <div className="spinner"></div>
          <p>Cargando logro especial...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="share-logros-container">
        <div className="share-logros-error">
          <h2>❌ Error</h2>
          <p>{error || 'No se encontró el logro especial'}</p>
        </div>
      </div>
    );
  }

  const { futbolista, logro, temporada } = data;
  const shareUrl = data.share_url || window.location.href;
  const pageTitle = `${futbolista.nombre} - ${logro.nombre} | Liga ProKids`;
  const jornadasTexto = logro.jornadas && logro.jornadas.length > 0 
    ? `Jornadas: ${logro.jornadas.join(', ')}` 
    : '';
  const pageDescription = `¡${futbolista.nombre} consiguió "${logro.nombre}"! ${logro.descripcion}. ${jornadasTexto}`;

  // Imagen para Open Graph
  const ogImage = `${window.location.origin}/images/logo-ligaprokids.png`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImage} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={shareUrl} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:image" content={ogImage} />
      </Helmet>

      <div className="share-logros-container">
        {/* Header */}
        <div className="share-logros-header">
          <img 
            src="/images/logo-ligaprokids.png" 
            alt="Liga ProKids" 
            className="share-logros-logo"
          />
          <h1 className="share-logros-title">Liga ProKids</h1>
        </div>

        {/* Contenido principal */}
        <div className="share-logros-content">
          {/* Logro Especial */}
          <div 
            className="share-nivel-card"
            style={{ borderColor: logro.color }}
          >
            <div className="share-nivel-icon" style={{ fontSize: '80px' }}>
              {logro.icono}
            </div>
            <h2 className="share-nivel-nombre" style={{ color: logro.color }}>
              {logro.nombre}
            </h2>
            <p className="share-nivel-descripcion">
              {logro.descripcion}
            </p>
            {logro.jornadas && logro.jornadas.length > 0 && (
              <div className="share-logro-jornadas">
                <span className="share-logro-jornadas-label">Jornadas:</span>
                <span className="share-logro-jornadas-numeros">
                  {logro.jornadas.join(', ')}
                </span>
              </div>
            )}
          </div>

          {/* Información del futbolista */}
          <div className="share-logros-futbolista">
            <h3>{futbolista.nombre} {futbolista.apellidos || ''}</h3>
            <p className="share-logros-temporada">Temporada {temporada.nombre}</p>
          </div>

          {/* Botones de compartir */}
          <div className="share-logros-social">
            <h4>Compartir en redes sociales:</h4>
            <div className="share-logros-buttons">
              <ShareSocialButton
                platform="whatsapp"
                url={shareUrl}
                title={`¡${futbolista.nombre} consiguió "${logro.nombre}"!`}
                text={pageDescription}
              />
              <ShareSocialButton
                platform="facebook"
                url={shareUrl}
                title={`¡${futbolista.nombre} consiguió "${logro.nombre}"!`}
                text={pageDescription}
              />
              <ShareSocialButton
                platform="twitter"
                url={shareUrl}
                title={`¡${futbolista.nombre} consiguió "${logro.nombre}"!`}
                text={pageDescription}
              />
              <ShareSocialButton
                platform="copy"
                url={shareUrl}
                title={`¡${futbolista.nombre} consiguió "${logro.nombre}"!`}
                text={pageDescription}
              />
            </div>
          </div>

          {/* CTA */}
          <div className="share-logros-cta">
            <p>¿Quieres que tu hijo/a también forme parte de Liga ProKids?</p>
            <a
              href="https://ligaprokids.es"
              className="share-logros-cta-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              Únete a Liga ProKids
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="share-logros-footer">
          <p>© {new Date().getFullYear()} Liga ProKids - Todos los derechos reservados</p>
        </div>
      </div>
    </>
  );
};

export default ShareLogroEspecial;

